import { useCallback, useState } from 'react'
import { useAnswerGateMutation } from '@/features/ai/studioApi'

export type AnswerGateFailure = 'expired' | 'run_closed' | 'forbidden' | 'not_found' | 'other'

const readError = (error: unknown): { status?: number; code?: string } => {
  const e = error as { status?: number; data?: { detail?: { code?: string } } } | undefined
  return { status: e?.status, code: e?.data?.detail?.code }
}

/**
 * Ответ автора на вопрос прогона. Отдельный хук, а не вызов мутации из
 * компонента: у ответа четыре отказа со своим смыслом, и разбирать их в JSX
 * значит завести вторую копию правила.
 *
 * Два отказа приходят под одним `409` и различаются ТОЛЬКО кодом в теле:
 * `gate_expired` — не ошибка клика, а состояние: сервер закрыл вопрос по
 * своему сроку, следующий тик поллера принесёт `state: "expired"` и карточка
 * скажет об этом сама. `gate_run_not_waiting` — прогон закрылся, пока автор
 * думал (упала параллельная ветка графа); отвечать уже нечему, и показывать
 * надо `error_message` самой джобы, которую принесёт тот же тик.
 *
 * Решение, признанное неверным (Р-1, финальное ревью): третий, ещё неизвестный
 * код 409 раньше молча падал в `expired` — состояние, которое `GATE_FAILURE_TEXT`
 * сознательно не печатает. Значит будущий код отвечал бы кликом в пустоту.
 * Теперь известные коды по-прежнему молчат, а всё остальное уходит в `other`
 * («не удалось отправить ответ») — ноль обратной связи невозможен.
 */
export const useAnswerGate = () => {
  const [answerGate, { isLoading }] = useAnswerGateMutation()
  const [failure, setFailure] = useState<AnswerGateFailure | null>(null)

  const answer = useCallback(
    async (jobId: number, gateId: number, optionKey: string): Promise<boolean> => {
      setFailure(null)
      try {
        await answerGate({ jobId, gateId, option_key: optionKey, text: null }).unwrap()
        return true
      } catch (error) {
        const { status, code } = readError(error)
        if (status === 409) {
          setFailure(
            code === 'gate_expired'
              ? 'expired'
              : code === 'gate_run_not_waiting'
                ? 'run_closed'
                : 'other',
          )
        } else if (status === 403) setFailure('forbidden')
        else if (status === 404) setFailure('not_found')
        else setFailure('other')
        return false
      }
    },
    [answerGate],
  )

  return { answer, isAnswering: isLoading, failure }
}
